import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuth } from '../../hooks/useAuth';
import { BrandLogo } from './BrandLogo';

/**
 * The front door — when there is one.
 *
 * Whether this gates is the deployment's answer, not this component's. With
 * Identity Studio configured it sends a signed-out visitor to `/login` and
 * remembers where they were going; without it, `isAuthenticated` is true for
 * everyone and this waves them through, because inventing a gate in front of an
 * API that checks nothing would be theatre.
 *
 * Even when it does gate, it is not the security boundary: hiding a route is not
 * authorization. The backend verifies a real token on every request and scopes
 * every row it serves to the person that token names. This exists so a
 * signed-out visitor lands somewhere that explains itself instead of on a wall
 * of failed requests.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="grid min-h-dvh place-items-center bg-bg">
        <div className="flex flex-col items-center gap-3">
          <BrandLogo variant="symbol" className="size-8 animate-pulse" />
          <p className="text-xs text-fg-muted">Loading your workspace…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login${next && next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} replace />;
  }

  return children;
}
